import { useState, useEffect } from 'react'
import { useWedding } from '../context/WeddingContext'
import { supabase } from '../lib/supabase'
import { NIGERIAN_BANKS } from '../lib/banks'
import { Gift, Shirt, Check, Lock, Pencil } from 'lucide-react'

// Couple-side: set up OR change the single verified payout account that receives
// BOTH cash gifts and Aso Ebi payments. NGN settles via Paystack; supported
// non-NGN currencies (GHS/KES/ZAR/UGX/TZS) settle via Flutterwave. USD/GBP/EUR
// settle to Western banks via a corridor we don't support yet.
const FLW_COUNTRY = { GHS: 'GH', KES: 'KE', ZAR: 'ZA', UGX: 'UG', TZS: 'TZ' }

export default function PayoutAccountSetup({ variant = 'gifts' }) {
  const { wedding, refreshWedding } = useWedding()
  const [open, setOpen]   = useState(false)
  const [bank, setBank]   = useState('')
  const [acct, setAcct]   = useState('')
  const [busy, setBusy]   = useState(false)
  const [error, setError] = useState('')
  const [flwBanks, setFlwBanks] = useState(null)

  const currency = wedding?.currency || 'NGN'
  const isNgn    = currency === 'NGN'
  const flwCountry = FLW_COUNTRY[currency]
  const useFlw   = !isNgn && !!flwCountry

  // For a Flutterwave currency, fetch the country's bank list once.
  useEffect(() => {
    if (!useFlw) return
    fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/flw-banks?country=${flwCountry}`, {
      headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
    }).then(r => r.json()).then(j => setFlwBanks(j.ok ? j.banks : [])).catch(() => setFlwBanks([]))
  }, [useFlw, flwCountry])

  if (!wedding) return null

  // Currencies we can't pay out to yet (USD/GBP/EUR → Western banks).
  if (!isNgn && !useFlw) {
    return (
      <div style={{ background:'rgba(196,130,106,0.06)', border:'1px solid rgba(196,130,106,0.2)', borderRadius:'0.875rem', padding:'0.8rem 1rem' }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:0, lineHeight:1.5 }}>
          Online payments (cash gifts &amp; Aso Ebi) aren't available for <strong>{currency}</strong> weddings yet — support is coming soon.
        </p>
      </div>
    )
  }

  const banks = useFlw ? (flwBanks || []) : NIGERIAN_BANKS

  const isAso = variant === 'asoebi'
  const Icon  = isAso ? Shirt : Gift
  const title = isAso ? 'Receive Aso Ebi payments' : 'Receive cash gifts'
  const desc  = isAso
    ? 'Add the account guests pay into for their fabric. Money goes straight to your bank — no business account, no sharing your number publicly.'
    : 'Let guests send money from your portal. It goes straight to your bank — no business account, no sharing your number publicly.'
  const cta   = isAso ? 'Set up payments' : 'Turn on cash gifts'

  const isOn = !!wedding.gifts_enabled
  const acctName = wedding.payout_account_name
  const last4    = wedding.payout_last4
  const bankDisp = wedding.payout_bank

  async function enable() {
    if (!bank || !acct.trim()) { setError('Pick your bank and enter your account number.'); return }
    setBusy(true); setError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const bankName = banks.find(b => b.code === bank)?.name || ''
      const fn = useFlw ? 'flw-setup-subaccount' : 'gift-setup-subaccount'
      const body = useFlw
        ? { account_bank: bank, account_number: acct.trim(), bank_name: bankName }
        : { bank_code: bank, account_number: acct.trim(), bank_name: bankName }
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${fn}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify(body),
      })
      const j = await res.json()
      if (!j.ok) { setError(j.error || 'Could not save your payout account.'); setBusy(false); return }
      await refreshWedding()
      setOpen(false); setBank(''); setAcct('')
    } catch (_) { setError('Something went wrong. Please try again.') }
    setBusy(false)
  }

  // ── Account is set up — show it + a way to change it ──
  if (isOn && !open) {
    return (
      <div style={{ background:'rgba(155,175,155,0.1)', border:'1px solid rgba(155,175,155,0.35)', borderRadius:'0.875rem', padding:'0.875rem 1rem' }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:'0.6rem' }}>
          <Check size={18} color="#5A7A5A" style={{ flexShrink:0, marginTop:2 }}/>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#3D5A3D', margin:0 }}>
              {isAso ? 'Aso Ebi payments are on 🎉' : 'Cash gifts are on 🎉'}
            </p>
            {acctName ? (
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#3D5A3D', margin:'0.15rem 0 0' }}>
                Settles to <strong>{bankDisp ? `${bankDisp} ` : ''}••{last4 || '••'}</strong>{acctName ? ` · ${acctName}` : ''}
              </p>
            ) : (
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#3D5A3D', margin:'0.15rem 0 0' }}>Guests pay online and it settles straight to your bank.</p>
            )}
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#6B6B6B', margin:'0.35rem 0 0', lineHeight:1.5 }}>
              This account receives <strong>both</strong> your cash gifts and Aso Ebi payments.
            </p>
            <button onClick={()=>{ setOpen(true); setError('') }} style={{ display:'inline-flex', alignItems:'center', gap:'0.3rem', marginTop:'0.5rem', background:'none', border:'none', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', fontWeight:700, color:'#C4826A', padding:0 }}>
              <Pencil size={11}/> Change account
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Setup / change form ──
  return (
    <div className="card" style={{ padding:'1.125rem 1.25rem', background:'linear-gradient(135deg,#FBF4EE,#F6EFE8)', border:'1px solid rgba(196,130,106,0.25)' }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:'0.6rem' }}>
        <Icon size={18} color="#C4826A" style={{ flexShrink:0, marginTop:2 }}/>
        <div style={{ flex:1 }}>
          <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', color:'#1A1A1A', margin:'0 0 0.2rem' }}>{isOn ? 'Change payout account' : title}</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:0, lineHeight:1.55 }}>
            {isOn ? 'Update the bank account that receives your cash gifts and Aso Ebi payments. We verify it before switching.' : desc}
          </p>

          {!open ? (
            <button onClick={()=>setOpen(true)} className="btn-primary" style={{ marginTop:'0.75rem', fontSize:'0.8rem', padding:'0.5rem 1.1rem' }}>{cta}</button>
          ) : (
            <div style={{ marginTop:'0.875rem', display:'flex', flexDirection:'column', gap:'0.625rem' }}>
              <select value={bank} onChange={e=>setBank(e.target.value)} className="input-field" style={{ fontSize:'0.85rem' }} disabled={useFlw && flwBanks === null}>
                <option value="">{useFlw && flwBanks === null ? 'Loading banks…' : 'Select your bank…'}</option>
                {banks.map(b => <option key={b.code} value={b.code}>{b.name}</option>)}
              </select>
              <input value={acct} onChange={e=>setAcct(e.target.value.replace(/\D/g,''))} className="input-field" inputMode="numeric" maxLength={12} placeholder="Account number" style={{ fontSize:'0.85rem' }}/>
              {error && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#C0392B', margin:0 }}>{error}</p>}
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <button onClick={()=>{ setOpen(false); setError('') }} className="btn-secondary" style={{ fontSize:'0.8rem', padding:'0.5rem 1rem' }}>Cancel</button>
                <button onClick={enable} disabled={busy} className="btn-primary" style={{ fontSize:'0.8rem', padding:'0.5rem 1.1rem' }}>{busy ? 'Verifying…' : (isOn ? 'Verify & update' : 'Verify & enable')}</button>
              </div>
              <p style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#9B9B9B', margin:0 }}>
                <Lock size={10}/> We verify the account name first, so payments can't be misdirected. Used for both cash gifts and Aso Ebi.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
